import http from "@services/httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyBookingInvitationPickProductResult } from "@lib/bookingInvitationPickProductResult"
import { notifyError, notifySuccess } from "@lib/helper"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import { SaleEventModel } from "@models/saleEvent/customerModel"

const getSaleEventAuditFieldLabels = (): Record<string, string> => ({
  Name:         L("SALE_POLICY_DETAIL_NAME"),
  Code:         L("CODE"),
  StartDate:    L("PROMOTION_DETAI_START_DATE"),
  EndDate:      L("PROMOTION_DETAI_END_DATE"),
  CartId:       L("CART"),
  Description:  L("SALE_POLICY_DETAIL_NOTE"),
  IsActive:     L("LOAN_PROGRAM_DETAIL_IS_ACTIVE"),
})

class SalesEventService {
  public async create(body: any) {
    const res = await http.post("api/services/app/SalesEvent/Create", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async update(body: any) {
    const res = await http.put("api/services/app/SalesEvent/Update", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async active(id: number, isActive) {
    const res = await http.post(
      "api/services/app/SalesEvent/Active",
      { id },
      { params: { isActive } }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  public async bulkActive(id: number[]) {
    const res = await http.post("api/services/app/SalesEvent/BulkActive", id)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  public async get(id: number): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/SalesEvent/Get", {
      params: { id },
    })
    const { result } = res.data
    return SaleEventModel.assign(result)
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/SalesEvent/GetAll", { params })
    return res.data.result
  }

  public async getAuditLog(id: any) {
    const res = await http.get("api/services/app/SalesEvent/GetAuditLogs", {
      params: { id },
    })
    const labels = getSaleEventAuditFieldLabels()
    return (res.data.result || []).map((row: any) => {
      ;(row.items || []).forEach((item: any) => {
        item.propertyName = labels[item.propertyName] ?? item.propertyName
      })
      return row
    })
  }

  public async getUnits(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetUnitInCart", {
      params,
    })
    return res.data.result
  }

  /**
   * Hạn mức quota người nước ngoài của giỏ hàng, theo từng loại sản phẩm.
   * BE trả `null` khi giỏ hàng chưa cấu hình dòng quota nào.
   */
  public async getCartQuotaForForeigner(cartId: number) {
    const res = await http.get(
      "api/services/app/SalesEvent/GetCartQuotaForForeigner",
      { params: { cartId } }
    )
    return (res.data.result ?? []) as any[]
  }

  public async getBookingInSaleEvent(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetBookingInCart", {
      params,
    })
    return res.data.result
  }

  public async getSaleEventHistory(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetAllNotificationHistory", {
      params,
    })
    return res.data.result
  }

  /*
  * Gửi thông báo
  * Body: {
    bookingInvitationIds: [10015,10016,10017],
    salesEventId: 1, (Id sự kiện)
    channel: 1, (1 là email , 2 là sms)
    notificationTemplateMapId: 10132 (Template mail)
  }
  * nếu là resend thì chỉ cần gửi
  * salesEventNotificationDetailIds: [10015,10016,10017] //danh sach resend chỉ gửi khi gửi resend
  *
  */

  public async resendNotification(params: any) {
    const res = await http.post("api/services/app/SalesEvent/SendNotification", params)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SEND_SUCCESSFULLY")
    )
    return res.data.result
  }

  /*
  * Danh sách phiếu mời booking
  * Body:[ 10015,10016,10017]
  */

  public async exportSaleEventBookingExportTicket(params: any, body: any) {
    const res = await http.post("api/services/app/SalesEvent/ExportedBookingTicket",
      body,
      {params},
      
    )
    return res.data.result
  }

  /*
  * Danh sách phiếu mời booking
  * Params: BookingInvitationIds [10015,10016,10017] 
  */
  public async getListBookingTicket(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetListBookingTicket", {
      params,
    })
    return res.data.result
  }

  /*
  * body: {
    ids: [1,2,3],
    status: 1
  }
  */
  
  public async changeSaleEventBookingStatus(body: any) {
    const res = await http.post("api/services/app/SalesEvent/ChangeSaleEventStatus", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }

  /*
  * params: {
    "unitId": 0,
    "isQuota": true || false,
    "unitPrice": 0,
    "unitPriceAfterVat": 0,
    "unitPriceWithQuota": 0,
    "unitPriceWithQuotaAfterVat": 0,
    "bookingId": 0,
    "salesEventId": 0
  }
  */

  public async BookingInvitationPickProduct(body: any) {
    const res = await http.post("api/services/app/SalesEvent/BookingInvitationPickProduct", body)
    const result = res.data.result
    notifyBookingInvitationPickProductResult(result)
    return result
  }

  /*
  * params: {
    bookingInvitationId: 1,
    salesEventId: 1,
    checkInType: 1
  }
  * checkInType: 1 cổng 2 ballonting 3 lấy STT 4 chọn sản phẩm
  */

  public async checkInBookingInvitation(params: any) {
    const res = await http.post("api/services/app/SalesEvent/CheckInBookingInvitation", params)
    return res.data.result
  }

  public async getCurrentBookingProcess(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetCurrentInprocessSalesEvent", {
      params,
    })
    return res.data.result
  }

  /*
  * params: {
    cartId: 1,
    bookingCode: 1
  }
  */

  public async getBookingCustomerJoin(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetBookingInvitationInformation", {
      params,
    })
    return res.data.result
  }

  /*
  * body: [
  {
    "id": 2, // Thêm mới để trống, update thông tin thì truyền vào
    "bookingCustomerId": 20487, // bắt buộc lấy ID từ danh sách bookingCustomer
    "repCustomerName": "Thanh2",
    "repPhoneNumber": "098777771",
    "repEmailAddress": "Thanh1@Gmail.com",
    "repIdentifierCardNumber": "1232132135434",
    "repIdIssuedPlace": "Quảng Ngãi",
    "repIdIssuedDate": "2022-09-01T04:30:23.041Z",
    "repCompanyTaxCode": "1111",
    "repAddress": "địa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉ",
    "repProvinceId": 8,
    "repDistrictId": 95,
    "repCountryId": 232
  },
  {
    "id": 3, // Thêm mới để trống, update thông tin thì truyền vào
    "bookingCustomerId": 20488, // bắt buộc lấy ID từ danh sách bookingCustomer
    "repCustomerName": "Thanh3",
    "repPhoneNumber": "098777773",
    "repEmailAddress": "Thanh3@Gmail.com",
    "repIdentifierCardNumber": "1232132135434",
    "repIdIssuedPlace": "Quảng Ngãi",
    "repIdIssuedDate": "2022-09-01T04:30:23.041Z",
    "repCompanyTaxCode": "1111",
    "repAddress": "địa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa chỉđịa ch3ỉ",
    "repProvinceId": 8,
    "repDistrictId": 95,
    "repCountryId": 232
  }
  ]
  */

  public async updateRepresentationCustomer(body: any) {
    const res = await http.put("api/services/app/SalesEvent/UpdateRepresentationCustomer", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }

  /*
  * body: {"ids": [1, 2,3 ],"status": 1}
  */

  public async updateStatusEvent(body: any) {
    const res = await http.post("api/services/app/SalesEvent/ChangeStatus", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }
  /*
  * params: {
    Id: 1, (Id thông báo)
  }
  */

  public async viewContentDetailNotification(params: any) {
    const res = await http.get("api/services/app/SalesEvent/ViewContentNotification", {
      params,
    })
    return res.data.result
  }

  public async getSalesEventConfiguration(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetSalesEventConfiguration", {
      params,
    })
    return res.data.result
  }

  public async createOrUpdateSalesEventConfiguration(body: any) {
    const res = await http.post("api/services/app/SalesEvent/CreateOrUpdateSalesEventConfiguration", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }

  /*
  * body: {
    bookingId: 1,
    salesEventId: 1,
    pickProductStatus: 1 // truyền 2 hoặc 4, với 2 là cập nhật fail, với 4 là retry cho phép pick product lại
  }
  */

  public async updatePickProductStatus(body: any) {
    const res = await http.put("api/services/app/SalesEvent/UpdateBookingInvitationPickProductStatusToRetry", body)
    return res.data.result
  }

  /*
  * params: {
    Id: 1, sale event id
  }
  */
  public async verifySalesEventCheckList(params: any) {
    const res = await http.get("api/services/app/SalesEvent/VerifySalesEventCheckList", {
      params,
    })
    return res.data.result
  }
  
  /*
  * params: {
    bookingId: 1,
  }
  */
  public async clearBookingUnit(params: any) {
    const res = await http.post("api/services/app/SalesEvent/ClearUnitInBooking", null, {
      params,
    })
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async getListStackingPlan(params: any) {
    const res = await http.get("api/services/app/SalesEvent/GetListStackingPlan", {
      params,
    })
    return res.data.result
  }

  public async saveCheckinSetting(body: {
    salesEventId: number
    requireCheckin: boolean
    requireBallotting: boolean
    requireCheckinSTT: boolean
    requireCheckinRoom: boolean
    jsonSetting: null | string
  }) {
    const res = await http.post("api/services/app/SalesEvent/SaveCheckinSetting", body)
    return res.data.result
  }

  public async getCheckinSetting(params: { salesEventId: number }) {
    const res = await http.get("api/services/app/SalesEvent/GetCheckinSetting", {
      params,
    })
    return res.data.result
  }
}

export default new SalesEventService()
